// Menu topic-input DOM overlay — extracted verbatim from MenuScene.ts (FACE&FURY S4 SPLIT).
// Behavior-frozen: method bodies are byte-identical to the monolith; only the signatures were rewritten
// to `export function name(this: MenuScene, …)` and bound back onto the scene as class fields.
import Phaser from 'phaser';
import { PALETTE, hex, FONT } from '../keys';
import { anyModalOpen } from '../ui';
import type { MenuScene } from '../scenes/MenuScene';

  // --- topic input overlay ---------------------------------------------------
export function createTopicInput(this: MenuScene, frame: Phaser.GameObjects.Rectangle): void {
    const el = document.createElement('input');
    el.type = 'text';
    el.placeholder = 'Planets, mammals, capitals...';
    el.autocomplete = 'off';
    el.spellcheck = false;
    el.maxLength = 60;
    Object.assign(el.style, {
      position: 'absolute',
      boxSizing: 'border-box',
      border: 'none',
      outline: 'none',
      background: 'transparent',
      color: hex(PALETTE.text),
      fontFamily: FONT.family,
      textAlign: 'center',
      zIndex: '20',
      padding: '0 10px',
    } as Partial<CSSStyleDeclaration>);
    el.addEventListener('keydown', (ev: KeyboardEvent) => {
      if (ev.key === 'Enter') {
        ev.preventDefault();
        this.submitTyped();
      }
    });
    document.body.appendChild(el);
    this.inputEl = el;

    // Keep the overlay glued to the framed slot (canvas-space -> page pixels), including on resize.
    const sync = (): void => this.positionInput(frame, el);
    sync();
    this.syncEvent = this.time.addEvent({ delay: 120, loop: true, callback: sync });
    this.scale.on(Phaser.Scale.Events.RESIZE, sync);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.scale.off(Phaser.Scale.Events.RESIZE, sync));
  }

  /** Map a canvas-space rect to absolute page pixels using the canvas bounding rect + display scale. */
export function positionInput(this: MenuScene, frame: Phaser.GameObjects.Rectangle, el: HTMLInputElement): void {
    const canvas = this.game.canvas;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const sx = rect.width / this.scale.width;
    const sy = rect.height / this.scale.height;
    const w = frame.width * sx;
    const h = frame.height * sy;
    const left = rect.left + (frame.x - frame.width / 2) * sx;
    const top = rect.top + (frame.y - frame.height / 2) * sy;
    el.style.left = `${Math.round(left)}px`;
    el.style.top = `${Math.round(top)}px`;
    el.style.width = `${Math.round(w)}px`;
    el.style.height = `${Math.round(h)}px`;
    el.style.fontSize = `${Math.max(13, Math.round(h * 0.4))}px`;
    // The topic <input> is a DOM overlay ABOVE the canvas, so a Phaser veil can't cover it — hide it
    // while the tutorial is up, a board is cooking, OR any modal panel is open (settings/stats/skins/
    // disclosure/…), so its placeholder/text never bleeds through the overlay.
    el.style.display = this.loading || this.onboarding || anyModalOpen() ? 'none' : 'block';
  }

export function hideTopicInput(this: MenuScene): void {
    if (this.inputEl) this.inputEl.style.display = 'none';
  }

export function teardownOverlay(this: MenuScene): void {
    this.inflight?.abort();
    this.inflight = undefined;
    this.syncEvent?.remove();
    this.syncEvent = undefined;
    this.loadingDotsEvent?.remove();
    this.loadingDotsEvent = undefined;
    if (this.inputEl && this.inputEl.parentNode) this.inputEl.parentNode.removeChild(this.inputEl);
    this.inputEl = undefined;
  }
